import { VariantUpdateEvent } from '@theme/events';

/** @typedef {ConstructorParameters<typeof VariantUpdateEvent>[0]} VariantUpdateResource */

const PRODUCT_CARD_SECTION_ID = 'section-rendering-product-card';
const INDEX_VIEW = 'pinned-index';
const PREVIEW_PARAMETERS = ['preview_theme_id', 'preview_theme_token', 'pb'];

/**
 * Pins an existing collection product card to the first position on page one.
 * The product must already belong to the current collection.
 */
async function pinFirstCollectionProduct() {
  const pageUrl = new URL(window.location.href);
  const pageNumber = Number.parseInt(pageUrl.searchParams.get('page') || '1', 10);

  if (Number.isFinite(pageNumber) && pageNumber > 1) return;

  const firstValue = pageUrl.searchParams.get('first')?.trim();
  if (!firstValue) return;

  const productReference = parseProductReference(firstValue);
  if (!productReference) return;

  const variantCandidate = pageUrl.searchParams.has('variant')
    ? pageUrl.searchParams.get('variant')
    : productReference.variantId;
  const requestedVariantId = variantCandidate && /^\d+$/.test(variantCandidate) ? variantCandidate : '';

  const resultsList = document.querySelector('results-list[section-id]');
  const grid = resultsList?.querySelector('[ref="grid"]');
  const sectionId = resultsList?.getAttribute('section-id');

  if (!(resultsList instanceof HTMLElement) || !(grid instanceof HTMLUListElement) || !sectionId) return;

  const firstPageItems = getFirstPageItems(grid);
  const productsPerPage = firstPageItems.length;
  if (productsPerPage === 0) return;

  if (resultsList.dataset.curatedLoadingEnabled !== 'true') {
    await pinProduct();
    return;
  }

  const loader = document.createElement('div');
  loader.className = 'curated-product-loader';
  loader.setAttribute('role', 'status');
  const loaderText = document.createElement('span');
  loaderText.className = 'curated-product-loader__text';
  loaderText.textContent = resultsList.dataset.curatedLoadingText || 'One sec, loading your tee...';
  loader.append(loaderText);
  const container = grid.parentElement;
  const previousBusy = grid.getAttribute('aria-busy');
  container.classList.add('curated-product-loading');
  grid.setAttribute('aria-busy', 'true');
  container.append(loader);

  try {
    await pinProduct();
  } finally {
    loader.remove();
    container.classList.remove('curated-product-loading');
    if (previousBusy === null) grid.removeAttribute('aria-busy');
    else grid.setAttribute('aria-busy', previousBusy);
  }

  async function pinProduct() {
    let productState = await fetchProductState(productReference.handle, requestedVariantId, pageUrl);
    if (!productState && requestedVariantId) {
      productState = await fetchProductState(productReference.handle, '', pageUrl);
    }
    if (!productState) return;

    let productItem = grid.querySelector(`[data-product-id="${productState.productId}"]`);

    if (!(productItem instanceof HTMLLIElement)) {
      const productPosition = await findProductPosition(productState.productId, productReference.handle, pageUrl);
      if (productPosition === null) return;

      const productPage = Math.floor(productPosition / productsPerPage) + 1;
      productItem = await fetchCollectionCard(productState.productId, productPage, sectionId, pageUrl);
      if (!(productItem instanceof HTMLLIElement)) return;
    }

    productItem.dataset.page = '1';
    grid.prepend(productItem);
    reindexGridItems(grid);

    if (requestedVariantId && productState.variant && String(productState.variant.id) === requestedVariantId) {
      await applyVariant(productItem, productState);
    }
  }
}

/**
 * @param {string} rawValue
 * @returns {{ handle: string, variantId: string } | null}
 */
function parseProductReference(rawValue) {
  const simpleHandle = rawValue.match(/^[a-z0-9][a-z0-9-]*$/i)?.[0];
  if (simpleHandle) return { handle: simpleHandle.toLowerCase(), variantId: '' };

  try {
    const productUrl = new URL(rawValue, window.location.origin);
    const segments = productUrl.pathname.split('/').filter(Boolean);
    const productsSegment = segments.lastIndexOf('products');
    const handle = productsSegment >= 0 ? segments[productsSegment + 1] : '';

    if (!handle || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) return null;

    return {
      handle: handle.toLowerCase(),
      variantId: productUrl.searchParams.get('variant') || '',
    };
  } catch (_error) {
    return null;
  }
}

/**
 * @param {string} handle
 * @param {string} requestedVariantId
 * @param {URL} pageUrl
 * @returns {Promise<{ productId: string, variant: VariantUpdateResource | null, document: Document } | null>}
 */
async function fetchProductState(handle, requestedVariantId, pageUrl) {
  const shopify = /** @type {Shopify & { routes?: { root?: string } }} */ (window.Shopify);
  const routesRoot = shopify.routes?.root || '/';
  const requestUrl = new URL(`${routesRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
  requestUrl.searchParams.set('section_id', PRODUCT_CARD_SECTION_ID);

  if (requestedVariantId) requestUrl.searchParams.set('variant', requestedVariantId);
  copyPreviewParameters(pageUrl, requestUrl);

  const response = await fetch(requestUrl, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
  if (!response.ok) return null;

  const document = new DOMParser().parseFromString(await response.text(), 'text/html');
  const productCard = document.querySelector('product-card[data-product-id]');
  const productId = productCard?.getAttribute('data-product-id');
  const variantJson = productCard?.querySelector('variant-picker script[type="application/json"]')?.textContent;

  if (!productId || !/^\d+$/.test(productId)) return null;

  /** @type {VariantUpdateResource | null} */
  let variant = null;
  if (variantJson) {
    try {
      variant = JSON.parse(variantJson);
    } catch (_error) {
      variant = null;
    }
  }

  return { productId, variant, document };
}

/**
 * @param {string} productId
 * @param {string} handle
 * @param {URL} pageUrl
 * @returns {Promise<number | null>}
 */
async function findProductPosition(productId, handle, pageUrl) {
  let indexPage = 1;
  let totalPages = 1;
  let offset = 0;

  do {
    const requestUrl = collectionRequestUrl(pageUrl);
    requestUrl.searchParams.set('view', INDEX_VIEW);
    requestUrl.searchParams.set('page', String(indexPage));

    const response = await fetch(requestUrl, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
    if (!response.ok) return null;

    const data = /** @type {{ items?: Array<{ id?: string | number, handle?: string }>, page_size?: number, pages?: number }} */ (
      await response.json()
    );
    if (!Array.isArray(data.items)) return null;

    const itemIndex = data.items.findIndex(
      (item) => String(item?.id) === productId || String(item?.handle) === handle
    );
    if (itemIndex >= 0) return offset + itemIndex;

    const pageSize = Number.parseInt(String(data.page_size), 10);
    totalPages = Number.parseInt(String(data.pages), 10);
    if (!Number.isFinite(pageSize) || pageSize < 1 || !Number.isFinite(totalPages) || totalPages < 1) return null;

    offset += pageSize;
    indexPage += 1;
  } while (indexPage <= totalPages);

  return null;
}

/**
 * @param {string} productId
 * @param {number} productPage
 * @param {string} sectionId
 * @param {URL} pageUrl
 * @returns {Promise<HTMLLIElement | null>}
 */
async function fetchCollectionCard(productId, productPage, sectionId, pageUrl) {
  const requestUrl = collectionRequestUrl(pageUrl);
  requestUrl.searchParams.set('section_id', sectionId);
  requestUrl.searchParams.set('page', String(productPage));

  const response = await fetch(requestUrl, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
  if (!response.ok) return null;

  const document = new DOMParser().parseFromString(await response.text(), 'text/html');
  const productItem = document.querySelector(`[data-product-id="${productId}"]`);

  return productItem instanceof HTMLLIElement ? productItem : null;
}

/**
 * @param {HTMLLIElement} productItem
 * @param {{ productId: string, variant: VariantUpdateResource | null, document: Document }} productState
 */
async function applyVariant(productItem, productState) {
  if (!productState.variant) return;

  await customElements.whenDefined('product-card');

  const productCard = productItem.querySelector('product-card');
  if (!(productCard instanceof HTMLElement)) return;

  productCard.dispatchEvent(
    new VariantUpdateEvent(productState.variant, String(productState.variant.id), {
      html: productState.document,
      productId: productState.productId,
    })
  );
}

/**
 * Builds a collection request that retains sorting, filtering, localization, and preview state.
 * Ad-curation parameters are removed so they cannot affect the rendered collection response.
 *
 * @param {URL} pageUrl
 */
function collectionRequestUrl(pageUrl) {
  const requestUrl = new URL(pageUrl.pathname, pageUrl.origin);

  for (const [key, value] of pageUrl.searchParams) {
    if (!['first', 'variant', 'source', 'limit', 'page', 'view', 'section_id'].includes(key)) {
      requestUrl.searchParams.append(key, value);
    }
  }

  return requestUrl;
}

/** @param {URL} sourceUrl @param {URL} targetUrl */
function copyPreviewParameters(sourceUrl, targetUrl) {
  for (const parameter of PREVIEW_PARAMETERS) {
    if (sourceUrl.searchParams.has(parameter)) {
      targetUrl.searchParams.set(parameter, sourceUrl.searchParams.get(parameter) || '');
    }
  }
}

/** @param {HTMLUListElement} grid */
function getFirstPageItems(grid) {
  const pageOneItems = Array.from(grid.querySelectorAll(':scope > li[data-page="1"]'));
  if (pageOneItems.length > 0) return pageOneItems;
  return Array.from(grid.querySelectorAll(':scope > li.product-grid__item'));
}

/** @param {HTMLUListElement} grid */
function reindexGridItems(grid) {
  const items = grid.querySelectorAll(':scope > li.product-grid__item');

  items.forEach((item, index) => {
    for (const className of Array.from(item.classList)) {
      if (className.startsWith('product-grid__item--')) item.classList.remove(className);
    }
    item.classList.add(`product-grid__item--${index}`);
  });
}

async function initializeCuratedProduct() {
  if (document.readyState !== 'complete') {
    await new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  }
  await pinFirstCollectionProduct();
}

initializeCuratedProduct().catch((error) => {
  console.warn('[Curated collection] Unable to pin the requested product.', error);
});
